import { FileUploadArgs, HttpRequestInit, SecurequClientResponse } from "./types";
import { chunkFile, getFileId, totalChunks } from "../include/file";
import Base from "./Base";
import fileScaner from '../include/file-scaner';
import { UploadFileMeta } from "../server/types";

export * from "./types"

class SecurequClient extends Base {

   async send(path: string, init?: HttpRequestInit): Promise<SecurequClientResponse> {
      await this.handshake();

      init = await this.hooksCall('beforeRequest', path, init) || init;
      const url = await this.url(path)
      if (url.pathname === '/') throw new Error("Path is not allowed")

      for (let key in init?.body) {
         if (init?.body[key] instanceof File) {
            init.body[key] = await this.uploadFile(init.body[key], init?.onProgress)
         }
      }

      const res = await this.fetch(path, init);
      if (["Signeture expired", "Request expired", "Invalid Signeture"].includes(res.message)) {
         await this.refreshHandshake()
         return await this.send(path, init);
      }
      await this.hooksCall('afterResponse', res);
      return res;
   }

   async uploadFile(file: FileUploadArgs, onProgress?: (p: number) => void): Promise<SecurequClientResponse> {
      const handshake = await this.handshake();

      if (!(file instanceof File)) {
         const controller = new AbortController();
         const chunk = file.chunk
         const filemeta = file.meta
         const chunkIndex = filemeta.chunkIndex;
         filemeta.isFinish = chunkIndex + 1 === filemeta.totalChunks;
         const fileId = filemeta.fileId
         const maxFileSize = handshake?.maxFileSize
         if (maxFileSize && filemeta.size > maxFileSize * 1024) {
            throw new Error(`File size exceeds the limit of ${maxFileSize / 1024} MB`)
         }

         const abort = async () => {
            await this.deleteFile(fileId);
            controller.abort();
            window.removeEventListener("pagehide", abort);
         }

         if (chunkIndex === 0 && handshake?.checkFileType && !fileScaner(chunk).valid) {
            await abort()
            return {
               success: false,
               message: "Unknown or unsupported file type",
               data: null,
               code: 0
            }
         }

         const info: any = {
            method: 'PUT',
            signal: controller.signal,
            body: {
               chunk,
               filemeta
            },
            onProgress: (p: number) => {
               if (onProgress) {
                  const totalProgress = Math.floor(((chunkIndex + 1) / filemeta.totalChunks) * 100)
                  const currentProgress = Math.floor((p / 100) / filemeta.totalChunks * 100)
                  onProgress(Math.min(totalProgress + currentProgress, 100))
               }
            }
         }

         await this.hooksCall('beforeUploadChunk', chunk, filemeta);

         let res = await this.fetch('/', info)

         if (!res.success) {
            if (["Signeture expired", "Request expired", "Invalid Signeture"].includes(res.message)) {
               window.removeEventListener("pagehide", abort);
               await this.refreshHandshake()
               res = await this.uploadFile(file, onProgress);
            } else {
               await abort()
               return res
            }
         }
         await this.hooksCall('afterUploadChunk', res, filemeta);
         window.removeEventListener("pagehide", abort);
         return {
            success: true,
            message: 'File uploaded successfully',
            data: filemeta,
            code: 200
         }
      } else {
         const fileId = await getFileId(file);
         const _file = await this.hooksCall('beforeUpload', file, fileId) || file;
         const chunkSize = handshake?.chunkSize

         // send metadata
         const filemeta: UploadFileMeta = {
            fileId: fileId,
            name: _file.name,
            size: _file.size,
            type: _file.type,
            totalChunks: totalChunks(_file, chunkSize),
            chunkIndex: 0,
            isFinish: false
         }

         for await (let { chunk, chunkIndex } of chunkFile(_file, chunkSize)) {
            filemeta.chunkIndex = chunkIndex;
            await this.uploadFile({
               chunk,
               meta: filemeta
            })
         }

         const info = {
            success: true,
            message: 'File uploaded successfully',
            data: filemeta,
            code: 200
         }
         await this.hooksCall('afterUpload', info, _file);
         return info
      }
   }

   async deleteFile(fileId: string, init?: Omit<HttpRequestInit, 'body' | 'method'>): Promise<SecurequClientResponse> {
      await this.handshake();
      const res = await this.fetch('/', {
         ...init,
         method: 'DELETE',
         body: { fileId }
      });
      if (["Signeture expired", "Request expired", "Invalid Signeture"].includes(res.message)) {
         await this.refreshHandshake()
         return await this.deleteFile(fileId, init);
      }
      return res;
   }

   async get(path: string, init?: Omit<HttpRequestInit, 'body' | 'method'>) {
      return await this.send(path, init);
   }

   async post(path: string, init?: Omit<HttpRequestInit, 'method'>) {
      return await this.send(path, { ...init, method: "POST" });
   }

   async put(path: string, init?: Omit<HttpRequestInit, 'method'>) {
      return await this.send(path, { ...init, method: "PUT" });
   }

   async delete(path: string, init?: Omit<HttpRequestInit, 'method'>) {
      return await this.send(path, { ...init, method: "DELETE" });
   }

}

export default SecurequClient;
